import { useState, useEffect, useRef, Fragment, ReactNode } from 'react';
import kit from './kit.module.css';
import styles from './UndoAgentActionDemo.module.css';

const COUNTDOWN_FROM = 10;

function useCountdown(active: boolean, onExpire?: () => void) {
  const [countdown, setCountdown] = useState(COUNTDOWN_FROM);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;

  function stop() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  useEffect(() => {
    if (!active) return;
    setCountdown(COUNTDOWN_FROM);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stop();
          expireRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return stop;
  }, [active]);

  return { countdown, stop };
}

function seconds(n: number) {
  return `${n} second${n !== 1 ? 's' : ''}`;
}

function Timer({ countdown }: { countdown: number }) {
  return (
    <div className={styles.timer} aria-hidden="true">
      <span className={styles.timerFill} style={{ width: `${(countdown / COUNTDOWN_FROM) * 100}%` }} />
    </div>
  );
}

function AgentLine({ children }: { children: ReactNode }) {
  return (
    <div className={kit.agent}>
      <span className={kit.agentIcon} aria-hidden="true">✦</span>
      <p>{children}</p>
    </div>
  );
}

// ── Email send scenario ───────────────────────────────────────────────────────

type EmailState = 'trigger' | 'sent' | 'undone';

function EmailSendScenario() {
  const [state, setState] = useState<EmailState>('trigger');
  const { countdown, stop } = useCountdown(state === 'sent');
  const expired = countdown === 0;

  return (
    <>
      <div className={kit.message}>Send my CV summary to the employer.</div>

      {state === 'trigger' && (
        <>
          <AgentLine>I'll compile a summary email and send it on your behalf.</AgentLine>
          <div className={kit.row}>
            <button className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setState('sent')}>
              Generate &amp; send CV summary
            </button>
          </div>
        </>
      )}

      {state === 'sent' && (
        <>
          <div className={kit.log}>
            <div className={kit.logRow}>
              <span className={kit.logTime}>0:00</span>
              <span className={`${kit.logDot} ${kit.logDotDone}`} />
              <span>Compiled CV summary</span>
              <span className={`${kit.chip} ${styles.status}`}>Done</span>
            </div>
            <div className={kit.logRow}>
              <span className={kit.logTime}>0:01</span>
              <span className={`${kit.logDot} ${kit.logDotDone}`} />
              <span>Sent to employer contact</span>
              <span className={`${kit.chip} ${kit.chipGood} ${styles.status}`}>Sent</span>
            </div>
          </div>

          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>Undo window</span>
              <span aria-live="polite">{expired ? 'Closed' : `${countdown}s left`}</span>
            </div>
            <h3 className={kit.proposalTitle}>Undo this action</h3>
            <p className={styles.body}>
              {expired
                ? 'The undo window has closed. This action can no longer be undone.'
                : `This will recall the email and return it to draft. Available for ${seconds(countdown)}.`}
            </p>
            <Timer countdown={countdown} />
            {!expired && (
              <div className={kit.row}>
                <button
                  className={`${kit.btn} ${kit.btnEdit}`}
                  onClick={() => {
                    stop();
                    setState('undone');
                  }}
                >
                  ↶ Undo send
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {state === 'undone' && (
        <>
          <AgentLine>Action reversed. The email was not sent - your CV draft is ready to review.</AgentLine>
          <div className={kit.row}>
            <span className={kit.chip}>↶ Recalled</span>
            <span className={kit.chip}>Draft restored</span>
          </div>
        </>
      )}
    </>
  );
}

// ── Document edit scenario ────────────────────────────────────────────────────

type DocState = 'idle' | 'edited' | 'undone' | 'expired';

const DIFF_CHANGES = [
  { type: 'change' as const, label: 'Job title', old: 'Retail Assistant', next: 'Senior Retail Associate' },
  { type: 'change' as const, label: 'Dates', old: '2019-2021', next: 'January 2019 - March 2021' },
  { type: 'add' as const, label: 'Added', old: '', next: 'Managed a team of 4 during peak periods' },
];

function DocumentEditScenario() {
  const [state, setState] = useState<DocState>('idle');
  const { countdown, stop } = useCountdown(state === 'edited', () => setState('expired'));

  return (
    <>
      <div className={kit.message}>Polish my CV using my profile data.</div>

      {state === 'idle' && (
        <>
          <AgentLine>I'll update 3 fields based on your profile data.</AgentLine>
          <div className={kit.row}>
            <button className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setState('edited')}>
              Apply AI suggestions to CV
            </button>
          </div>
        </>
      )}

      {state === 'edited' && (
        <>
          <AgentLine>CV updated - 3 changes made. You can undo them for a few seconds.</AgentLine>
          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>What changed</span>
              <span>3 edits</span>
            </div>
            <dl className={`${kit.fields} ${styles.diff}`}>
              {DIFF_CHANGES.map((change) => (
                <Fragment key={change.label}>
                  <dt>{change.label}</dt>
                  <dd>
                    {change.type === 'change' ? (
                      <>
                        <s className={styles.diffOld}>{change.old}</s>
                        <span aria-hidden="true"> → </span>
                        <span className={styles.diffNew}>{change.next}</span>
                      </>
                    ) : (
                      <span className={styles.diffNew}>+ {change.next}</span>
                    )}
                  </dd>
                </Fragment>
              ))}
            </dl>
            <hr className={`${kit.divider} ${styles.gap}`} />
            <p className={styles.body} aria-live="polite">
              Undo restores the CV to before the AI edits. Available for {seconds(countdown)}.
            </p>
            <Timer countdown={countdown} />
            <div className={kit.row}>
              <button
                className={`${kit.btn} ${kit.btnEdit}`}
                onClick={() => {
                  stop();
                  setState('undone');
                }}
              >
                ↶ Undo edits
              </button>
            </div>
          </div>
        </>
      )}

      {state === 'undone' && (
        <>
          <AgentLine>Changes reversed. Your CV is back to its previous version.</AgentLine>
          <div className={kit.row}>
            <span className={kit.chip}>↶ Restored</span>
          </div>
        </>
      )}

      {state === 'expired' && (
        <>
          <AgentLine>The changes are now saved. You can still edit them manually in the CV editor.</AgentLine>
          <div className={kit.row}>
            <span className={`${kit.chip} ${kit.chipGood}`}>✓ Saved</span>
          </div>
        </>
      )}
    </>
  );
}

// ── Top-level component with scenario tabs ────────────────────────────────────

type Tab = 'email' | 'document';

const TABS: { id: Tab; label: string }[] = [
  { id: 'email', label: 'Email send' },
  { id: 'document', label: 'Document edit' },
];

export function UndoAgentActionDemo() {
  const [tab, setTab] = useState<Tab>('email');

  return (
    <div className={kit.root}>
      <div className={kit.row} role="tablist" aria-label="Scenario">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`${kit.chip} ${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div key={tab} className={kit.stack}>
        {tab === 'email' ? <EmailSendScenario /> : <DocumentEditScenario />}
      </div>
    </div>
  );
}
